/**
 * Zod validation schemas for Purchase Order types
 */

import { z } from 'zod';
import type {
  PurchaseOrderResponse,
  PurchaseOrderItemResponse,
  CreatePurchaseOrderRequest,
  CreatePurchaseOrderItemRequest,
  UpdatePOStatusRequest,
  UpdatePOPaymentRequest,
  DeliveryItemRequest,
} from '@kisanlink/erp-service';

export const purchaseOrderItemSchema = z.object({
  id: z.string(),
  po_id: z.string(),
  variant_id: z.string(),
  product_name: z.string(),
  product_sku: z.string(),
  quantity: z.number().positive('Quantity must be positive'),
  unit_price: z.number().nonnegative('Unit price cannot be negative'),
  line_total: z.number().nonnegative('Line total cannot be negative'),
  received_quantity: z.number().nonnegative('Received quantity cannot be negative'),
  // GST breakdown fields
  base_price: z.number().nonnegative().optional(),
  gst_rate: z.number().nonnegative().optional(),
  gst_amount: z.number().nonnegative().optional(),
  cgst_rate: z.number().nonnegative().optional(),
  cgst_amount: z.number().nonnegative().optional(),
  sgst_rate: z.number().nonnegative().optional(),
  sgst_amount: z.number().nonnegative().optional(),
  igst_rate: z.number().nonnegative().optional(),
  igst_amount: z.number().nonnegative().optional(),
  // NEW: Line item GST totals (per-unit × quantity)
  gst_amount_total: z.number().nonnegative().optional(),
  cgst_amount_total: z.number().nonnegative().optional(),
  sgst_amount_total: z.number().nonnegative().optional(),
  igst_amount_total: z.number().nonnegative().optional(),
  created_at: z.string(),
}) satisfies z.ZodType<PurchaseOrderItemResponse>;

export const purchaseOrderSchema = z.object({
  id: z.string(),
  po_number: z.string(),
  collaborator_id: z.string(),
  collaborator_name: z.string(),
  warehouse_id: z.string(),
  warehouse_name: z.string(),
  order_date: z.string(),
  expected_delivery_date: z.string(),
  actual_delivery_date: z.string().optional(),
  status: z.enum(['placed', 'confirmed', 'out_for_delivery', 'delivered', 'verified', 'paid']),
  payment_status: z.enum(['unpaid', 'partial', 'paid']),
  paid_amount: z.number().nonnegative(),
  total_amount: z.number().nonnegative(),
  // NEW: GST total fields
  total_base_amount: z.number().nonnegative().optional(),
  total_gst_amount: z.number().nonnegative().optional(),
  total_cgst_amount: z.number().nonnegative().optional(),
  total_sgst_amount: z.number().nonnegative().optional(),
  total_igst_amount: z.number().nonnegative().optional(),
  total_rejected_amount: z.number().nonnegative().optional(),
  amount_owed: z.number().nonnegative().optional(),
  is_inter_state: z.boolean().nullable().optional(),
  items: z.array(purchaseOrderItemSchema),
  created_at: z.string(),
  updated_at: z.string(),
}) satisfies z.ZodType<PurchaseOrderResponse>;

export const createPurchaseOrderItemSchema = z.object({
  variant_id: z.string().min(1, 'Variant ID is required'),
  quantity: z.number().positive('Quantity must be positive'),
  unit_price: z.number().nonnegative('Unit price cannot be negative'),
}) satisfies z.ZodType<CreatePurchaseOrderItemRequest>;

export const createPurchaseOrderSchema = z.object({
  collaborator_id: z.string().min(1, 'Collaborator ID is required'),
  warehouse_id: z.string().min(1, 'Warehouse ID is required'),
  order_date: z.string().optional(),
  expected_delivery_date: z.string().min(1, 'Expected delivery date is required'),
  // NEW: is_inter_state field (null=auto-detect, true=IGST, false=CGST+SGST)
  is_inter_state: z.boolean().nullable().optional(),
  items: z.array(createPurchaseOrderItemSchema).min(1, 'At least one item is required'),
}) satisfies z.ZodType<CreatePurchaseOrderRequest>;

export const deliveryItemSchema = z.object({
  po_item_id: z.string(),
  expiry_date: z.string(),
  accept: z.boolean().optional(),
  received_quantity: z.number().nonnegative().optional(),
  accepted_quantity: z.number().nonnegative().optional(),
  batch_number: z.string().optional(),
  rejection_reason: z.string().optional(),
}) satisfies z.ZodType<DeliveryItemRequest>;

export const updatePOStatusSchema = z.object({
  status: z.string().min(1, 'Status is required'),
  actual_delivery_date: z.string().optional(),
  accept_all: z.boolean().optional(),
  default_expiry_date: z.string().optional(),
  items: z.array(deliveryItemSchema).optional(),
}) satisfies z.ZodType<UpdatePOStatusRequest>;

export const updatePOPaymentSchema = z.object({
  payment_status: z.enum(['unpaid', 'partial', 'paid']),
  paid_amount: z.number().nonnegative('Paid amount cannot be negative'),
}) satisfies z.ZodType<UpdatePOPaymentRequest>;

export type PurchaseOrder = z.infer<typeof purchaseOrderSchema>;
export type PurchaseOrderItem = z.infer<typeof purchaseOrderItemSchema>;
export type CreatePurchaseOrder = z.infer<typeof createPurchaseOrderSchema>;
export type CreatePurchaseOrderItem = z.infer<typeof createPurchaseOrderItemSchema>;
export type UpdatePOStatus = z.infer<typeof updatePOStatusSchema>;
export type UpdatePOPayment = z.infer<typeof updatePOPaymentSchema>;
export type DeliveryItem = z.infer<typeof deliveryItemSchema>;
